/*
 * カテゴリナビゲーション（sections/category-nav.liquid）
 *
 * タブの表示切替のみを担当する。全タブの DOM はサーバ側で出力済みで、
 * 非アクティブなパネルは hidden 属性で隠してあるだけなので取得処理は無い。
 *
 * 矢印キーはフォーカス移動のみ（手動アクティベーション）で、切替は Enter / Space。
 * 非同期取得が無いので自動アクティベーションでも成立するが、同じページに乗る
 * top-ranking のタブが手動アクティベーション（あちらはパネルが非同期取得のため
 * 自動だとキー操作のたびに fetch が走る）なので、キーボード操作の挙動を
 * 揃えることを優先している。
 *
 * ⚠️ タブ操作のロジックは assets/top-ranking.js と似ているが、あちらは
 *    fetch・キャッシュ・中断処理と一体になっている。共通化すると top-ranking の
 *    挙動を変えることになるため、意図的に別実装にしてある。
 */

const CATEGORY_NAV_SELECTORS = {
  tab: '[data-category-nav-tab]',
  tablist: '[data-category-nav-tablist]',
  tabbar: '[data-category-nav-tabbar]',
  panel: '[data-category-nav-panel]',
  scroll: '[data-category-nav-scroll]',
};

class CategoryNav extends HTMLElement {
  constructor() {
    super();

    this.resizeObserver = null;
    this.scrollFrame = null;

    this.onTabClick = this.onTabClick.bind(this);
    this.onTabKeydown = this.onTabKeydown.bind(this);
    this.onScrollClick = this.onScrollClick.bind(this);
    this.onTablistScroll = this.onTablistScroll.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
  }

  connectedCallback() {
    this.tabbar = this.querySelector(CATEGORY_NAV_SELECTORS.tabbar);
    this.tablist = this.querySelector(CATEGORY_NAV_SELECTORS.tablist);
    this.tabs = Array.from(this.querySelectorAll(CATEGORY_NAV_SELECTORS.tab));
    this.panels = Array.from(this.querySelectorAll(CATEGORY_NAV_SELECTORS.panel));

    if (!this.panels.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick);
      tab.addEventListener('keydown', this.onTabKeydown);
    });

    this.setupScrollButtons();

    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    (this.tabs || []).forEach((tab) => {
      tab.removeEventListener('click', this.onTabClick);
      tab.removeEventListener('keydown', this.onTabKeydown);
    });

    (this.scrollButtons || []).forEach((button) => {
      button.removeEventListener('click', this.onScrollClick);
    });

    if (this.tablist) this.tablist.removeEventListener('scroll', this.onTablistScroll);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  /* ------------------------------------------------------------------
   * タブ切替
   * ------------------------------------------------------------------ */

  onTabClick(event) {
    this.selectTab(event.currentTarget);
  }

  onTabKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex = -1;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    const nextTab = this.tabs[nextIndex];
    // roving tabindex: フォーカス可能なタブは常に1つだけ
    event.currentTarget.tabIndex = -1;
    nextTab.tabIndex = 0;
    nextTab.focus();
    this.scrollTabIntoView(nextTab);
  }

  onBlockSelect(event) {
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;

    // タブブロックが選択された場合はそのタブへ
    const tab = this.tabs.find((item) => item.dataset.blockId === blockId);
    if (tab) {
      this.selectTab(tab);
      return;
    }

    // 項目ブロックが選択された場合は、それを含むパネルのタブへ。
    // blockId をセレクタに埋めると id 次第で SyntaxError になるため属性値を直接見る。
    const owner = this.panels.find((panel) =>
      Array.from(panel.querySelectorAll('[data-shopify-editor-block]')).some((node) =>
        (node.getAttribute('data-shopify-editor-block') || '').includes(blockId)
      )
    );
    if (!owner) return;

    const ownerTab = this.tabs.find((item) => item.dataset.index === owner.dataset.index);
    if (ownerTab) this.selectTab(ownerTab);
  }

  selectTab(tab) {
    const panel = this.panels.find((item) => item.dataset.index === tab.dataset.index);
    if (!panel) return;

    const previous = this.panels.find((item) => !item.hidden);

    // 非表示にするパネルの中にフォーカスが残っているとフォーカスを失うので退避する
    if (previous && previous !== panel && previous.contains(document.activeElement)) {
      tab.focus();
    }

    this.tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.tabIndex = selected ? 0 : -1;
      item.classList.toggle('is-active', selected);
    });

    this.panels.forEach((item) => {
      item.hidden = item !== panel;
    });

    this.scrollTabIntoView(tab);
  }

  /* ------------------------------------------------------------------
   * タブバーの横スクロール
   * ------------------------------------------------------------------ */

  setupScrollButtons() {
    if (!this.tabbar || !this.tablist) return;

    this.scrollButtons = Array.from(this.tabbar.querySelectorAll(CATEGORY_NAV_SELECTORS.scroll));
    this.scrollButtons.forEach((button) => button.addEventListener('click', this.onScrollClick));

    this.tablist.addEventListener('scroll', this.onTablistScroll);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateScrollButtons());
      this.resizeObserver.observe(this.tablist);
    }

    this.updateScrollButtons();
  }

  onScrollClick(event) {
    const direction = event.currentTarget.dataset.categoryNavScroll === 'prev' ? -1 : 1;
    this.tablist.scrollBy({ left: direction * this.tablist.clientWidth * 0.8, behavior: 'smooth' });
  }

  onTablistScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => this.updateScrollButtons());
  }

  updateScrollButtons() {
    if (!this.tabbar || !this.tablist || !this.scrollButtons) return;

    const { scrollLeft, clientWidth, scrollWidth } = this.tablist;
    const scrollable = scrollWidth - clientWidth > 1;

    this.tabbar.classList.toggle('category-nav__tabbar--scrollable', scrollable);

    this.scrollButtons.forEach((button) => {
      if (button.dataset.categoryNavScroll === 'prev') {
        button.disabled = !scrollable || scrollLeft <= 1;
      } else {
        button.disabled = !scrollable || scrollLeft + clientWidth >= scrollWidth - 1;
      }
    });
  }

  // タブが見切れている場合だけ横方向に寄せる。
  // scrollIntoView はページ全体を縦スクロールさせることがあるので使わない。
  scrollTabIntoView(tab) {
    if (!this.tablist) return;

    const listRect = this.tablist.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const margin = 16;

    if (tabRect.left < listRect.left) {
      this.tablist.scrollBy({ left: tabRect.left - listRect.left - margin, behavior: 'smooth' });
    } else if (tabRect.right > listRect.right) {
      this.tablist.scrollBy({ left: tabRect.right - listRect.right + margin, behavior: 'smooth' });
    }
  }
}

if (!customElements.get('category-nav')) {
  customElements.define('category-nav', CategoryNav);
}
